
import { Box, FormHelperText, Grid, makeStyles, Typography } from "@material-ui/core";
import { useOneToolStore } from "../../../hooks/useOneToolStore";
import { OneToolCardBorder } from "../../common/oneToolCard";
import { FORM_MAX_WIDTH } from "../../../utils/constants";
import { RoundedBackgroundIcon } from "../../../components/roundedBackgroundIcon";
import MapOutlinedIcon from '@material-ui/icons/MapOutlined';
import { TitleDivider } from "../../../components/titleDivider";
import {
  CurrencyField,
  MultiSelectField,
  NumberFormat,
  RadioField,
  SelectField,
  Slider,
  TextField,
  yesNoOptions,
} from '@roc/ui';
import { floridaCountiesRestriction, isNotBlank, notLicensedSfr, propertyCitiesRestriction, propertyStates } from "@roc/feature-utils";
import { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";
import { PropertyTypesForm } from "./propertyTypeForm";
import { PortfolioDetailsForm } from "./portfolioDetailsForm";
import { ErrorOutline } from "@material-ui/icons";

const areSelectedCountiesValid = (counties, selectedCounties) => {
  if (!selectedCounties) {
    return false;
  }
  const countyValues = new Set(counties.map(c => c.value));
  return selectedCounties.every(sc => countyValues.has(sc.value));
};

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    position: 'relative',
    maxWidth: 1300,
    margin: 'auto',
  },
  root: {
    display: 'flex',
    backgroundColor: '#FFF4E5',
    borderRadius: '8px',
    marginTop: '16px',
    padding: '14px 16px',
    alignItems: 'center',
  },
  icon: {
    color: '#EF6C00',
    marginRight: '12px',
  },
  title: {
    fontWeight: 'bold',
    color: '#663C00',
  },
}));

export const PortfolioInformationForm = observer(() => {

  const { oneToolStore } = useOneToolStore();
  const { rentalPortfolioQuoteStore } = oneToolStore;
  const { portfolioInformationFormStore } = rentalPortfolioQuoteStore;
  const [counties, setCounties] = useState([]);

  const { form } = portfolioInformationFormStore;

  const error = form.fields.propertyCounties.error;

  const classes = useStyles();

  useEffect(() => {
    portfolioInformationFormStore.fetchStateCounties();
  }, [portfolioInformationFormStore]);

  useEffect(() => {
    if (form.fields.propertyStates.value) {
      setCounties(portfolioInformationFormStore.getCounties());

    } else {
      setCounties([]);
    }
    portfolioInformationFormStore.loadStateLicenseMessage();
  }, [form.fields.propertyStates.value]);

  const handleChangeState = (value) => {
    portfolioInformationFormStore.onFieldChange("propertyStates", value);
    cleanupPropertyCountiesIfInvalid();

  }

  const cleanupPropertyCountiesIfInvalid = () => {
    const counties = portfolioInformationFormStore.getCounties()
    const obj: any = portfolioInformationFormStore.getFlattenedValues();
    if (!areSelectedCountiesValid(counties, obj.propertyCounties)) {
      portfolioInformationFormStore.onFieldChange('propertyCounties', null);
    }
  }

  return (
    <Box display="flex" flexDirection="column" gridGap="20px">
      <OneToolCardBorder>
        <Grid
          container
          spacing={2}
          className={classes.container}
          style={{ maxWidth: FORM_MAX_WIDTH, marginBottom: '0.5rem' }}
        >
          <Grid item xs={12}>
            <Grid
              container
              spacing={2}
              justifyContent="flex-start"
              alignItems="center"
            >
              <Grid item>
                <RoundedBackgroundIcon
                  Icon={MapOutlinedIcon}
                  iconColor="#6D6D6D"
                />
              </Grid>
              <Grid item>
                <Typography variant="h4" style={{ color: '#6D6D6D' }}>
                  Portfolio Properties
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={6}>
            <SelectField
              label={'Select Property State'}
              name={'propertyStates'}
              value={form.fields.propertyStates.value}
              error={isNotBlank(form.fields.propertyStates.error)}
              errorText={form.fields.propertyStates.error}
              options={propertyStates}
              onChange={handleChangeState}
              variant="outlined"
              fullWidth
              shrink
              required
              testId="propertyStates"
            />
            <FormHelperText>{notLicensedSfr.message}</FormHelperText>
            {portfolioInformationFormStore.stateLicenseMessage && (
              <FormHelperText>{portfolioInformationFormStore.stateLicenseMessage}</FormHelperText>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <MultiSelectField
              label={'Select Property State'}
              name={'propertyCounties'}
              options={counties}
              getOptionSelected={(option, selectedValue) => {
                return option.value == selectedValue.value;
              }}
              value={
                form.fields.propertyCounties.value
                  ? form.fields.propertyCounties.value
                  : []
              }
              onChange={(e, value) => {
                portfolioInformationFormStore.onFieldChange('propertyCounties', value);
              }}
              renderInput={params => (
                <TextField
                  helperText={error}
                  error={isNotBlank(error)}
                  testId="propertyCounties"
                  {...params}
                  variant="outlined"
                />
              )}
              fullWidth
              required
              testId="propertyCounties"
            />
          </Grid>
          {portfolioInformationFormStore.isRestrictedBaltimoreCity && (
            <Box className={classes.root} style={{ backgroundColor: '#FDEDED' }}>
              <ErrorOutline className={classes.icon} style={{ color: '#D32F2F' }} />
              <Typography
                className={classes.title}
                variant="body1"
                style={{ color: '#5F2120' }}
              >
                {propertyCitiesRestriction.message}
              </Typography>
            </Box>
          )}
          {portfolioInformationFormStore.isRestrictedFloridaCounty && (
            <Box className={classes.root} style={{ backgroundColor: '#FDEDED' }}>
              <ErrorOutline className={classes.icon} style={{ color: '#D32F2F' }} />
              <Typography
                className={classes.title}
                variant="body1"
                style={{ color: '#5F2120' }}
              >
                {floridaCountiesRestriction.message}
              </Typography>
            </Box>
          )}
          <Grid
            item
            xs={12}
            style={{ marginTop: '1rem', marginBottom: '1rem' }}
          >
            <TitleDivider title="Property Types" />
            <PropertyTypesForm portfolioInformationFormStore={portfolioInformationFormStore} />
          </Grid>
        </Grid>
      </OneToolCardBorder>
      <OneToolCardBorder>
        <PortfolioDetailsForm portfolioInformationFormStore={portfolioInformationFormStore} />
      </OneToolCardBorder>
    </Box>
  );

});
